<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Fields</h1>
    <p class="text-muted mb-0">Manage your farm fields and locations</p>
  </div>
  <button class="btn btn-primary" (click)="addField()">
    <i class="bi bi-plus-lg me-2"></i>Add Field
  </button>
</div>

<!-- Stats Cards -->
<div class="row g-3 mb-4">
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle text-primary">
            <i class="bi bi-geo-alt"></i>
          </div>
          <div>
            <div class="stat-value text-primary">{{ fieldCount() }}</div>
            <div class="stat-label text-muted">Total Fields</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle text-success">
            <i class="bi bi-rulers"></i>
          </div>
          <div>
            <div class="stat-value text-success">{{ totalAcreage() | number:'1.1-1' }}</div>
            <div class="stat-label text-muted">Total Acres</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-info-subtle text-info">
            <i class="bi bi-flower3"></i>
          </div>
          <div>
            <div class="stat-value text-info">{{ cropTypes().length }}</div>
            <div class="stat-label text-muted">Crop Types</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Search and Filters -->
<div class="card mb-4">
  <div class="card-body p-3">
    <div class="row g-3 align-items-end">
      <div class="col-md-4">
        <label class="form-label">Search</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input type="text" class="form-control" placeholder="Search by name, address, city..."
                 [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)">
        </div>
      </div>
      <div class="col-md-3">
        <label class="form-label">Crop Type</label>
        <select class="form-select" [ngModel]="selectedCropType()" (ngModelChange)="selectedCropType.set($event)">
          <option value="">All Crops</option>
          <option *ngFor="let crop of cropTypes()" [value]="crop">{{ crop }}</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">State</label>
        <select class="form-select" [ngModel]="selectedState()" (ngModelChange)="selectedState.set($event)">
          <option value="">All States</option>
          <option *ngFor="let state of states()" [value]="state">{{ state }}</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">Customer</label>
        <select class="form-select" [ngModel]="selectedCustomerId()" (ngModelChange)="selectedCustomerId.set($event)">
          <option value="">All Customers</option>
          <option *ngFor="let customer of customerOptions()" [value]="customer.id">{{ customer.name }}</option>
        </select>
      </div>
      <div class="col-md-1">
        <label class="form-label">&nbsp;</label>
        <button class="btn btn-outline-secondary w-100" (click)="clearFilters()"
                [disabled]="!searchQuery() && !selectedCropType() && !selectedState() && !selectedCustomerId()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Fields List -->
<div class="card">
  <div class="card-header d-flex justify-content-between align-items-center p-3">
    <h5 class="mb-0">Fields</h5>
    <span class="badge bg-secondary">{{ fields().length }} fields</span>
  </div>
  <div class="card-body p-0">
    <ng-container *ngIf="fields().length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Customer</th>
              <th>Acreage</th>
              <th>Crop</th>
              <th>Location</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let field of fields()">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="field-icon">
                    <i class="bi bi-geo-alt-fill"></i>
                  </div>
                  <div>
                    <div class="fw-medium">
                      {{ field.name }}
                      <i *ngIf="field.agremoFieldId"
                         class="bi bi-leaf text-success ms-1 small"
                         title="Synced to Agremo"></i>
                      <i *ngIf="field.agremoFieldId && !field.hasBoundary"
                         class="bi bi-exclamation-triangle text-warning ms-1 small"
                         title="Synced to Agremo but no boundary defined"></i>
                      <i *ngIf="field.hasBoundary"
                         class="bi bi-bounding-box text-info ms-1 small"
                         title="Boundary defined"></i>
                    </div>
                    <div class="small text-muted" *ngIf="field.notes">{{ field.notes }}</div>
                  </div>
                </div>
              </td>
              <td>
                <a *ngIf="field.customerId && getCustomerName(field.customerId)"
                   [routerLink]="['/customers', field.customerId, 'view']"
                   class="text-decoration-none">
                  {{ getCustomerName(field.customerId) }}
                </a>
                <span *ngIf="!field.customerId" class="text-muted">&mdash;</span>
              </td>
              <td>
                <span class="fw-medium">{{ field.acreage | number:'1.1-1' }}</span>
                <span class="text-muted"> ac</span>
              </td>
              <td>
                <span class="badge bg-success-subtle text-success">{{ field.cropType }}</span>
              </td>
              <td>
                <div *ngIf="field.city || field.state">
                  {{ field.city }}<span *ngIf="field.city && field.state">, </span>{{ field.state }}
                </div>
                <div class="small text-muted" *ngIf="field.address">{{ field.address }}</div>
                <span *ngIf="!field.city && !field.state && !field.address" class="text-muted">&mdash;</span>
              </td>
              <td class="text-end">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="editField(field)" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger" (click)="deleteField(field)" title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="bi bi-geo-alt"></i>
        </div>
        <h5>No Fields Found</h5>
        <p class="text-muted mb-3" *ngIf="!searchQuery() && !selectedCropType() && !selectedState() && !selectedCustomerId()">
          Add your first field to start tracking your farm locations.
        </p>
        <p class="text-muted mb-3" *ngIf="searchQuery() || selectedCropType() || selectedState() || selectedCustomerId()">
          No fields match your current filters. Try adjusting your search criteria.
        </p>
        <button class="btn btn-primary" (click)="addField()" *ngIf="!searchQuery() && !selectedCropType() && !selectedState() && !selectedCustomerId()">
          <i class="bi bi-plus-lg me-2"></i>Add Field
        </button>
        <button class="btn btn-outline-secondary" (click)="clearFilters()" *ngIf="searchQuery() || selectedCropType() || selectedState() || selectedCustomerId()">
          <i class="bi bi-x-lg me-2"></i>Clear Filters
        </button>
      </div>
    </ng-template>
  </div>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
